import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, ChevronDown, ChevronUp, LogOut } from "lucide-react";
import { useAuth } from "@/app/AuthContext";
import { displayUserName, userInitials } from "@/lib/userName";
import { PRIMARY_NAV, USER_MENU_NAV } from "@/app/navigation";
import { TaskPill } from "@/components/layout/TaskPill";
import { SectionLabel } from "@/components/ui/SectionLabel";

/**
 * The chrome every screen sits in.
 *
 * Sticky rather than gaia-app's `position: fixed`, and it carries the top safe-area inset itself.
 * A fixed bar is positioned against the viewport, so on an installed iOS PWA with
 * `viewport-fit=cover` it sits behind the status bar no matter what padding the body has.
 */
export function Navbar() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  const name = displayUserName(user);
  const groups = user?.groups ?? [];
  // Only when there is exactly one. Picking the first of several would be arbitrary - that is what
  // the old `roles[0]` did, and it showed whichever endpoint name happened to sort first.
  const singleGroup = groups.length === 1 ? groups[0] : undefined;

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-surface pt-[env(safe-area-inset-top)]">
      <div className="flex h-[var(--navbar-height)] items-center justify-between gap-4 px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <img src="/logo.svg" alt="Interstellar Lab" className="h-7 w-7" />
          <span className="text-xs font-semibold uppercase tracking-[0.08em] text-text">
            Biopod
          </span>
        </Link>

        {/* Placeholder figures: nothing schedules protocols yet. Wire to the real source when a
            tasks feature exists - the props are already the shape it will supply. */}
        <TaskPill taskCount={3} nextTaskTime="10:00" newCount={1} />

        <div className="flex shrink-0 items-center gap-4">
          <nav className="flex items-center gap-1">
            {PRIMARY_NAV.map((item) => {
              const active = pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? "page" : undefined}
                  className={`rounded-sm px-3 py-2 text-sm transition-colors ${
                    active
                      ? "font-medium text-text"
                      : "text-text-muted hover:bg-neutral-50 hover:text-text"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              className="flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-2
                         text-sm text-text transition-colors hover:bg-neutral-50"
            >
              <span className="relative flex">
                <Bell className="h-4 w-4 text-text-muted" aria-hidden />
              </span>
              <span className="font-medium">
                {name}
                {singleGroup !== undefined && (
                  <span className="text-text-muted"> · {singleGroup}</span>
                )}
              </span>
              {menuOpen ? (
                <ChevronUp className="h-3.5 w-3.5 text-text-muted" aria-hidden />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 text-text-muted" aria-hidden />
              )}
            </button>

            {menuOpen && (
              <>
                {/* Catches the click that dismisses the menu, including on touch. */}
                <div className="fixed inset-0 z-90" onClick={() => setMenuOpen(false)} />
                <div
                  role="menu"
                  className="absolute right-0 top-[calc(100%+var(--space-2))] z-100 w-60 overflow-hidden
                             rounded-md border border-border bg-surface shadow-lg"
                >
                  <div className="flex items-center gap-3 border-b border-border p-4">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full
                                 bg-podpink-500 text-sm font-semibold text-white"
                    >
                      {userInitials(user)}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-text">{name}</div>
                      {user?.email !== undefined && (
                        <div className="truncate text-sm text-text-muted">{user.email}</div>
                      )}
                    </div>
                  </div>

                  {groups.length > 0 && (
                    <div className="flex flex-col gap-0.5 border-b border-border px-4 py-3">
                      <SectionLabel>{groups.length === 1 ? "Group" : "Groups"}</SectionLabel>
                      {/* The groups, not the roles they grant. A role is one endpoint name, an
                          account holds dozens, and the list filled the menu to no one's benefit.
                          Group names are written as Cognito has them, scope and all, so they can
                          be matched against the console when access is in question. */}
                      <span className="text-sm font-medium break-words text-text">
                        {groups.join(", ")}
                      </span>
                    </div>
                  )}

                  <div className="py-2">
                    {USER_MENU_NAV.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          role="menuitem"
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-text
                                     transition-colors hover:bg-neutral-50"
                        >
                          {Icon !== undefined && (
                            <Icon className="h-3.5 w-3.5 text-text-muted" aria-hidden />
                          )}
                          {item.label}
                        </Link>
                      );
                    })}
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => void logout()}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm
                                 text-red-500 transition-colors hover:bg-neutral-50"
                    >
                      <LogOut className="h-3.5 w-3.5" aria-hidden />
                      Log out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
